import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import { transformFirstLetterToUpperCase } from '@/core/helpers/utils';
import ToolService from '@/core/services/Tool.service';
import type {
  IPhieuXuatKhoSuppliesQuantityRow,
  PhieuXuatKhoFooterAmountLine,
  PhieuXuatKhoTableFooterMeta,
  PhieuXuatKhoTableRow,
} from '../types';
import { calcThanhTien, calcThanhTienTotal } from './phieuXuatKhoCalculations';

interface PickFooterOptions {
  showPickFooter: Ref<boolean>;
  quantityRows: Ref<IPhieuXuatKhoSuppliesQuantityRow[]>;
  displayItems: ComputedRef<PhieuXuatKhoTableRow[]>;
  appendSummaryFooterRows: (
    lines: PhieuXuatKhoTableRow[],
    meta: PhieuXuatKhoTableFooterMeta
  ) => PhieuXuatKhoTableRow[];
}

export function usePhieuXuatKhoPickFooter({
  showPickFooter,
  quantityRows,
  displayItems,
  appendSummaryFooterRows,
}: PickFooterOptions) {
  const pickTotalAmount = computed(() =>
    quantityRows.value.reduce(
      (sum, item) =>
        sum + calcThanhTien(item.don_gia_trong_kho, item.so_luong_se_lay),
      0
    )
  );

  const pickTotalText = ref('');

  watch(
    pickTotalAmount,
    async (total) => {
      if (!showPickFooter.value || total <= 0) {
        pickTotalText.value = '';
        return;
      }

      const {
        data: { data },
      } = await ToolService.convertPriceToText(total);

      pickTotalText.value = transformFirstLetterToUpperCase(data);
    },
    { immediate: true }
  );

  const pickFooterLines = computed((): PhieuXuatKhoFooterAmountLine[] =>
    quantityRows.value
      .filter(({ so_luong_se_lay }) => so_luong_se_lay > 0)
      .map((row) => ({
        don_gia: row.don_gia_trong_kho,
        so_luong_lay: row.so_luong_se_lay,
      }))
  );

  const pickTableData = computed(() => {
    const rows = displayItems.value;

    if (!showPickFooter.value || !rows.length) {
      return rows;
    }

    return appendSummaryFooterRows(rows, {
      tong_tien_bang_chu: pickTotalText.value,
      vat_tu_da_lay: pickFooterLines.value,
    });
  });

  return {
    pickFooterLines,
    pickTableData,
    pickTotalAmount,
    pickTotalText,
    calcThanhTien,
    calcThanhTienTotal,
  };
}
